/**
 * Firestore Database Service (web) — port of services/firebase/firestore.ts
 * onto the Firebase JS SDK. Same static method names as mobile.
 */
import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc, addDoc, onSnapshot,
  query, where, orderBy, limit as qLimit, serverTimestamp, arrayUnion, arrayRemove, documentId,
  type DocumentSnapshot, type QueryDocumentSnapshot, type DocumentData,
} from 'firebase/firestore';
import { db, auth } from '@/lib/firebase';
import { COLLECTIONS } from './config';
import { DiaryService } from './diary';
import type { UserProfile, Meal, MealInsight, ResponseLog, ImportedRecipe } from '@/types';

type Snap = DocumentSnapshot<DocumentData> | QueryDocumentSnapshot<DocumentData>;

function toDate(v: unknown): Date | undefined {
  if (!v) return undefined;
  const d = typeof (v as { toDate?: () => Date }).toDate === 'function' ? (v as { toDate: () => Date }).toDate() : v instanceof Date ? v : undefined;
  return d && !Number.isNaN(d.getTime()) ? d : undefined;
}

function normalizeHealthSync(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object') return raw;
  const { lastSyncAt, cursors, ...rest } = raw as Record<string, unknown>;
  const out: Record<string, unknown> = { ...rest };
  const last = toDate(lastSyncAt);
  if (last) out.lastSyncAt = last;
  if (cursors && typeof cursors === 'object') {
    const c: Record<string, Date> = {};
    for (const k of Object.keys(cursors as object)) {
      const d = toDate((cursors as Record<string, unknown>)[k]);
      if (d) c[k] = d;
    }
    out.cursors = c;
  }
  return out;
}

export function normalizeProfile(id: string, data: Record<string, unknown>): UserProfile {
  const knownTriggers = Array.isArray(data.knownTriggers) ? data.knownTriggers : [];
  return {
    ...data,
    id,
    knownTriggers,
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
    ...(data.healthSync ? { healthSync: normalizeHealthSync(data.healthSync) } : {}),
  } as unknown as UserProfile;
}

export function normalizeMealDoc(snap: Snap): Meal {
  const data = (snap.data() || {}) as Record<string, unknown>;
  const conv = data.aiConversation as { timestamp?: unknown }[] | undefined;
  const response = data.response as { loggedAt?: unknown } | undefined;
  return {
    ...data,
    id: snap.id,
    eatenAt: toDate(data.eatenAt),
    analyzedAt: toDate(data.analyzedAt),
    responseReminderSentAt: toDate(data.responseReminderSentAt),
    reminderTime: toDate(data.reminderTime),
    aiConversation: conv?.map((m) => ({ ...m, timestamp: toDate(m.timestamp) })),
    response: response ? { ...response, loggedAt: toDate(response.loggedAt) } : undefined,
  } as unknown as Meal;
}

function normalizeRecipe(snap: Snap): ImportedRecipe {
  const data = (snap.data() || {}) as Record<string, unknown>;
  const source = data.source as { importedAt?: unknown } | undefined;
  return {
    ...data,
    id: snap.id,
    createdAt: toDate(data.createdAt),
    source: source ? { ...source, importedAt: toDate(source.importedAt) } : undefined,
  } as unknown as ImportedRecipe;
}

export class FirestoreService {
  // ---------------------------------------------------------------- users
  static async createUserProfile(userId: string, data: Partial<UserProfile>): Promise<void> {
    await setDoc(
      doc(db, COLLECTIONS.USERS, userId),
      { notificationsEnabled: true, theme: 'system', isPremium: false, ...data, id: userId, createdAt: serverTimestamp(), updatedAt: serverTimestamp() },
      { merge: true },
    );
  }

  static async getUserProfile(userId: string): Promise<UserProfile | null> {
    const snap = await getDoc(doc(db, COLLECTIONS.USERS, userId));
    if (!snap.exists()) return null;
    return normalizeProfile(snap.id, snap.data() as Record<string, unknown>);
  }

  /** Accepts dot-path keys (e.g. 'optionalNutrients.dailyCaloriesLimit') and FieldValue sentinels. */
  static async updateUserProfile(userId: string, updates: Partial<UserProfile> | Record<string, unknown>): Promise<void> {
    await updateDoc(doc(db, COLLECTIONS.USERS, userId), { ...(updates as Record<string, unknown>), updatedAt: serverTimestamp() });
  }

  static async toggleFavoriteRecipe(userId: string, recipeId: string): Promise<boolean> {
    const ref = doc(db, COLLECTIONS.USERS, userId);
    const snap = await getDoc(ref);
    const favorites: string[] = (snap.data()?.favoriteRecipeIds as string[]) || [];
    const isFavorited = favorites.includes(recipeId);
    await updateDoc(ref, { favoriteRecipeIds: isFavorited ? arrayRemove(recipeId) : arrayUnion(recipeId), updatedAt: serverTimestamp() });
    return !isFavorited;
  }

  static async updatePremiumStatus(userId: string, isPremium: boolean): Promise<void> {
    await updateDoc(doc(db, COLLECTIONS.USERS, userId), { isPremium, updatedAt: serverTimestamp() });
  }

  static subscribeToUserProfile(
    userId: string,
    onUpdate: (profile: UserProfile | null, isAuthoritative?: boolean) => void,
    onError?: (error: Error) => void,
  ): () => void {
    return onSnapshot(
      doc(db, COLLECTIONS.USERS, userId),
      { includeMetadataChanges: true },
      (snap) => {
        if (!snap.exists()) {
          onUpdate(null, !snap.metadata.fromCache);
          return;
        }
        onUpdate(normalizeProfile(snap.id, snap.data() as Record<string, unknown>), true);
      },
      (err) => onError?.(err),
    );
  }

  // ---------------------------------------------------------------- daily summaries
  static async saveDailySummary(userId: string, date: string, payload: Record<string, unknown>): Promise<void> {
    await setDoc(doc(db, COLLECTIONS.USERS, userId, 'dailySummaries', date), { ...payload, updatedAt: serverTimestamp() }, { merge: true });
  }

  /** dailySummaries docs for an inclusive 'YYYY-MM-DD' range (doc id == date). */
  static async getDailySummaries(userId: string, startDate: string, endDate: string): Promise<Record<string, Record<string, unknown>>> {
    const q = query(collection(db, COLLECTIONS.USERS, userId, 'dailySummaries'), where(documentId(), '>=', startDate), where(documentId(), '<=', endDate));
    const snap = await getDocs(q);
    const out: Record<string, Record<string, unknown>> = {};
    snap.docs.forEach((d) => {
      out[d.id] = d.data();
    });
    return out;
  }

  // ---------------------------------------------------------------- meals
  static async createMeal(meal: Omit<Meal, 'id'>): Promise<string> {
    const ref = await addDoc(collection(db, COLLECTIONS.MEALS), { ...meal, eatenAt: meal.eatenAt || serverTimestamp() });
    return ref.id;
  }

  static async createVoiceLogMeal(userId: string, mealDescription: string): Promise<string> {
    const ref = await addDoc(collection(db, COLLECTIONS.MEALS), {
      userId, source: 'voice_log', mealDescription, photoUrl: '', aiConversation: [], eatenAt: serverTimestamp(),
    });
    return ref.id;
  }

  static async getMeal(mealId: string): Promise<Meal | null> {
    const snap = await getDoc(doc(db, COLLECTIONS.MEALS, mealId));
    return snap.exists() ? normalizeMealDoc(snap) : null;
  }

  static async getUserMeals(userId: string, limit = 50): Promise<Meal[]> {
    const q = query(collection(db, COLLECTIONS.MEALS), where('userId', '==', userId), orderBy('eatenAt', 'desc'), qLimit(limit));
    const snap = await getDocs(q);
    return snap.docs.map(normalizeMealDoc);
  }

  static subscribeToUserMeals(userId: string, limit: number, onUpdate: (meals: Meal[]) => void, onError?: (error: Error) => void): () => void {
    const q = query(collection(db, COLLECTIONS.MEALS), where('userId', '==', userId), orderBy('eatenAt', 'desc'), qLimit(limit));
    return onSnapshot(q, (snap) => onUpdate(snap.docs.map(normalizeMealDoc)), (err) => onError?.(err));
  }

  static subscribeToMeal(mealId: string, onUpdate: (meal: Meal | null) => void, onError?: (error: Error) => void): () => void {
    return onSnapshot(
      doc(db, COLLECTIONS.MEALS, mealId),
      (snap) => onUpdate(snap.exists() ? normalizeMealDoc(snap) : null),
      (err) => onError?.(err),
    );
  }

  static subscribeToUserVoiceLogs(userId: string, limit: number, onUpdate: (meals: Meal[]) => void, onError?: (error: Error) => void): () => void {
    const q = query(collection(db, COLLECTIONS.MEALS), where('userId', '==', userId), where('source', '==', 'voice_log'), orderBy('eatenAt', 'desc'), qLimit(limit));
    return onSnapshot(q, (snap) => onUpdate(snap.docs.map(normalizeMealDoc)), (err) => onError?.(err));
  }

  static subscribeToUserPhotoMeals(userId: string, limit: number, onUpdate: (meals: Meal[]) => void, onError?: (error: Error) => void): () => void {
    const q = query(collection(db, COLLECTIONS.MEALS), where('userId', '==', userId), orderBy('eatenAt', 'desc'), qLimit(limit + 20));
    return onSnapshot(
      q,
      (snap) => onUpdate(snap.docs.map(normalizeMealDoc).filter((m) => m.source !== 'voice_log').slice(0, limit)),
      (err) => onError?.(err),
    );
  }

  static async updateMeal(mealId: string, updates: Partial<Meal> | Record<string, unknown>): Promise<void> {
    await updateDoc(doc(db, COLLECTIONS.MEALS, mealId), updates as Record<string, unknown>);
  }

  static async addResponseToMeal(mealId: string, response: ResponseLog): Promise<void> {
    await updateDoc(doc(db, COLLECTIONS.MEALS, mealId), { response });
  }

  static async deleteMeal(mealId: string): Promise<void> {
    await deleteDoc(doc(db, COLLECTIONS.MEALS, mealId));
    // Cascade: remove any diary entries that reference this meal (owner-scoped).
    const uid = auth.currentUser?.uid;
    if (uid) DiaryService.deleteEntriesBySourceId(uid, mealId).catch(() => {});
  }

  static async getLifetimeScans(userId: string): Promise<number> {
    const snap = await getDoc(doc(db, COLLECTIONS.USERS, userId));
    return snap.exists() ? ((snap.data()?.lifetimeScansUsed as number) || 0) : 0;
  }

  // ---------------------------------------------------------------- insights
  static async saveInsights(userId: string, insights: MealInsight): Promise<void> {
    await addDoc(collection(db, COLLECTIONS.INSIGHTS), { ...insights, userId, generatedAt: serverTimestamp() });
  }

  static async getLatestInsights(userId: string): Promise<MealInsight | null> {
    const q = query(collection(db, COLLECTIONS.INSIGHTS), where('userId', '==', userId), orderBy('generatedAt', 'desc'), qLimit(1));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const data = snap.docs[0].data();
    return { ...data, generatedAt: toDate(data.generatedAt), periodStart: toDate(data.periodStart), periodEnd: toDate(data.periodEnd) } as MealInsight;
  }

  // ---------------------------------------------------------------- imported recipes
  static async getImportedRecipeById(recipeId: string): Promise<ImportedRecipe | null> {
    const snap = await getDoc(doc(db, COLLECTIONS.IMPORTED_RECIPES, recipeId));
    return snap.exists() ? normalizeRecipe(snap) : null;
  }

  static subscribeToUserImportedRecipes(userId: string, limit: number, onUpdate: (recipes: ImportedRecipe[]) => void, onError?: (error: Error) => void): () => void {
    const q = query(collection(db, COLLECTIONS.IMPORTED_RECIPES), where('importedByUserId', '==', userId), orderBy('createdAt', 'desc'), qLimit(limit));
    return onSnapshot(q, (snap) => onUpdate(snap.docs.map(normalizeRecipe)), (err) => onError?.(err));
  }

  static async updateImportedRecipe(recipeId: string, updates: Partial<ImportedRecipe>): Promise<void> {
    await updateDoc(doc(db, COLLECTIONS.IMPORTED_RECIPES, recipeId), { ...(updates as Record<string, unknown>), updatedAt: serverTimestamp() });
  }

  static async deleteImportedRecipe(recipeId: string): Promise<void> {
    await deleteDoc(doc(db, COLLECTIONS.IMPORTED_RECIPES, recipeId));
  }

  static async getUserImportedRecipes(userId: string, limit = 50): Promise<ImportedRecipe[]> {
    const q = query(collection(db, COLLECTIONS.IMPORTED_RECIPES), where('importedByUserId', '==', userId), orderBy('createdAt', 'desc'), qLimit(limit));
    const snap = await getDocs(q);
    return snap.docs.map(normalizeRecipe);
  }
}
